import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
// import 'leaflet/dist/leaflet.css';
import FileUploader from '../FileUploader';
import FileList from '../FileList';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import QRCode from 'qrcode';
//import GlassCard from '../common/GlassCard';
//import FileUploadButton from '../common/FileUploadButton';        these three imports needs are not used here, they should be used instead of importing glasscard from maindashboard
//import StatusBadge from '../common/StatusBadge';
import { 
  AlertCircle, 
  CheckCircle, 
  Clock,
  Home,
  Calendar,
  Layers,
  Users,
  MessageSquare,
  FileText,
  MapPin,
  Edit2,
  Download,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Ban,
  Upload,
  BarChart2,
  Folder,
  FolderOpen,
  Eye,
  Trash2,
  CalendarIcon,
  DollarSign,
  Target,
  Activity,
  Building2,
  Plus,
  Construction,
  Timer,
  Settings,
  X,
  Rocket,
  Check,
  MoreVertical
} from 'lucide-react';
import PhasesTab from './ProjectDetail/PhasesTab'; // Adjust the path as necessary
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Cover } from '../ui/Cover';


// Delete default Leaflet icon configuration
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const GlassCard = ({ children, className = '' }) => (
  <div className={`
    bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg 
    border border-white/20 hover:shadow-xl transition-all duration-300 
    ${className}
  `}>
    {children}
  </div>
);

const NavButton = ({ active, icon: Icon, children, onClick, count }) => (
  <button
    onClick={onClick}
    className={`
      flex items-center px-6 py-3 rounded-xl font-medium 
      transition-all duration-200 relative
      ${active 
        ? 'bg-blue-700 text-white shadow-lg' 
        : 'text-gray-600 hover:bg-gray-50 hover:text-slate-800'
      }
    `}
  >
    <Icon className="w-5 h-5 mr-2" />
    <span>{children}</span>
    {count !== undefined && (
      <span className={`
        absolute -top-2 -right-2 w-5 h-5 text-xs
        flex items-center justify-center rounded-full
        bg-red-500 text-white
        border-2 border-white
        font-semibold
      `}>
        {count}
      </span>
    )}
  </button>
);

const StatusBadge = ({ status, isMobile }) => {
  const config = {
    'Completed': {
      icon: CheckCircle2,
      class: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      iconClass: 'text-emerald-500'
    },
    'In Progress': {
      icon: Clock3,
      class: 'bg-violet-100 text-violet-700 border-violet-200',
      iconClass: 'text-violet-500'
    },
    'Not Started': {
      icon: Ban,
      class: 'bg-slate-100 text-slate-700 border-slate-200',
      iconClass: 'text-slate-500'
    },
    'Delayed': {
      icon: AlertTriangle,
      class: 'bg-rose-100 text-rose-700 border-rose-200',
      iconClass: 'text-rose-500'
    },
    'Pending': {
      icon: Clock3,
      class: 'bg-amber-100 text-amber-700 border-amber-200',
      iconClass: 'text-amber-500'
    }
  };

  const { icon: Icon, class: className, iconClass } = config[status];

  return (
    <div className={`
      inline-flex items-center rounded-full border
      transition-all duration-200
      ${className}
      ${isMobile 
        ? 'px-1.5 py-0.5 text-[11px] font-medium shadow-sm' // Smaller, more compact on mobile
        : 'px-3 py-1.5 text-sm font-medium'} // Original size on desktop
    `}>
      <Icon className={`
        ${iconClass}
        ${isMobile ? 'w-2.5 h-2.5 mr-0.5' : 'w-4 h-4 mr-1.5'}
      `} />
      {status}
    </div>
  );
};

const FileUploadButton = ({ onUpload }) => {
  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onUpload({
        name: file.name,
        size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
        uploadDate: new Date().toISOString().split('T')[0]
      });
    }
  };

  return (
    <div className="relative group">
      <input
        type="file"
        onChange={handleChange}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
      />
      <div className="
        flex items-center justify-center px-6 py-3 
        border-2 border-dashed border-indigo-300 
        rounded-xl bg-indigo-50/50 
        group-hover:bg-indigo-100/50
        transition-all duration-200
      ">
        <Upload className="w-5 h-5 text-indigo-500 mr-2" />
        <span className="text-indigo-600 font-medium">Upload Files</span>
      </div>
    </div>
  );
};
const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [activePhase, setActivePhase] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDetails, setEditedDetails] = useState({});
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [launchStep, setLaunchStep] = useState(0);
  const [isLaunching, setIsLaunching] = useState(false);
  const [currentSiteNumber, setCurrentSiteNumber] = useState(42); // Example starting number
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const navItems = [
    { id: 'overview', icon: Home, label: 'Overview' },
    { id: 'timeline', icon: Calendar, label: 'Timeline' },
    { id: 'phases', icon: Construction, label: 'Phases' },
    { id: 'files', icon: FileText, label: 'Files' },
    { id: 'team', icon: Users, label: 'Team' },
    !isMobile && { id: 'tasks', icon: CheckCircle, label: 'Tasks', count: '5' },
    !isMobile && { id: 'comments', icon: MessageSquare, label: 'Comments', count: '3' },
    isMobile && { id: 'more', icon: MoreVertical, label: 'More', count: '8' }
  ].filter(Boolean);

  const launchSteps = [
    { text: "Site Selection Verified" },
    { text: "Design Phase Completed" },
    { text: "Construction Completed" },
    { text: "Opening Preparations Done" },
    { text: "Handing Over to Operations" }
  ];

  useEffect(() => {
    fetchProjectData();
  }, [id]);

  useEffect(() => {
    if (showLaunchModal) {
      const timer = setInterval(() => {
        setLaunchStep(prev => {
          if (prev === launchSteps.length - 1) {
            clearInterval(timer);
            return prev;
          }
          return prev + 1;
        });
      }, 1500);

      return () => clearInterval(timer);
    }
  }, [showLaunchModal]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is typical tablet/mobile breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchProjectData = async () => {
    // Simulating API call
    const mockProject = {
      id,
      name: 'Store A Renovation',
      status: 'In Progress',
      currentPhase: 1,
      location: {
        latitude: 24.7136,
        longitude: 46.6753,
      },
      assignedTeam: [
        { name: 'Chris', designation: 'Project Manager', tasks: 12, completed: 8 },
        { name: 'Mohammed', designation: 'Lead Designer', tasks: 8, completed: 5 },
        { name: 'Khairo', designation: 'Construction Supervisor', tasks: 15, completed: 10 },
      ],
      phases: [
        { 
          name: 'Site Selection', 
          status: 'Completed', 
          files: [{ name: 'Site_Survey_Report.pdf', size: '2.5 MB', uploadDate: '2024-05-15' }], 
          startDate: '2024-05-01', 
          endDate: '2024-07-15',
          progress: 100
        },
        { 
          name: 'Design', 
          status: 'In Progress', 
          files: [], 
          startDate: '2024-08-16', 
          endDate: '2024-09-31',
          progress: 75
        },
        { 
          name: 'Construction', 
          status: 'In Progress', 
          files: [], 
          startDate: '2024-10-01', 
          endDate: '2024-12-31',
          progress: 30
        },
        { 
          name: 'Opening', 
          status: 'Not Started', 
          files: [], 
          startDate: '2025-01-01', 
          endDate: '2025-01-31',
          progress: 0
        },
      ],
      metrics: {
        progress: 65,
        budgetSpent: 43,
        daysLeft: 45,
        riskLevel: 'Medium',
        totalBudget: 'SAR 500,000',
        spentBudget: 'SAR 215,000',
        startDate: '2024-01-15',
        endDate: '2025-06-30',
        permitNumber: 'BLD-2024-0123',
        siteArea: '5000 sq ft',
        buildingType: 'Commercial Retail',
        zoning: 'C-2 (Commercial)'
      },
      description: 'Renovation project for Store A to modernize the interior and expand the retail space.',
      budget: 'SAR 500,000',
      comments: [
        { id: 1, author: 'Mohammed', text: 'Design phase is progressing well.', date: '2023-03-15' },
        { id: 2, author: 'Chris', text: 'We need to review the latest blueprints.', date: '2023-03-20' },
      ],
      tasks: [
        { id: 1, title: 'Finalize floor plans', status: 'In Progress', assignee: 'Chris', deadline: '2024-08-30' },
        { id: 2, title: 'Order materials', status: 'Pending', assignee: 'Mohammed', deadline: '2024-09-15' },
        { id: 3, title: 'Site inspection', status: 'Completed', assignee: 'Khairo', deadline: '2024-08-10' },
        { id: 4, title: 'Contractor meeting', status: 'Not Started', assignee: 'Chris', deadline: '2024-09-01' },
        { id: 5, title: 'Budget review', status: 'In Progress', assignee: 'Mohammed', deadline: '2024-08-25' },
      ],
    };

    setProject(mockProject);
    setEditedDetails(mockProject);
  };

  const handleFileUpload = useCallback((phase, file) => {
    setProject(prev => ({
      ...prev,
      phases: prev.phases.map((p, index) => 
        index === phase ? { ...p, files: [...p.files, file] } : p
      )
    }));
  }, []);

  const handleEditSubmit = useCallback(() => {
    setProject(editedDetails);
    setIsEditing(false);
  }, [editedDetails]);

  const handleTaskStatusChange = useCallback((taskId, newStatus) => {
    setProject(prevProject => ({
      ...prevProject,
      tasks: prevProject.tasks.map(task => 
        task.id === taskId ? { ...task, status: newStatus } : task
      )
    }));
  }, []);

  const handleCommentSubmit = useCallback((newComment) => {
    setProject(prevProject => ({
      ...prevProject,
      comments: [...prevProject.comments, { 
        id: Date.now(),
        author: 'Current User',
        text: newComment,
        date: new Date().toISOString().split('T')[0]
      }]
    }));
  }, []);

  const generatePDF = useCallback(async () => {
    const doc = new jsPDF();
    
    // QR Code
    const siteVisitNumber = 'SV' + Math.floor(1000 + Math.random() * 9000);
    const qrCodeDataUrl = await QRCode.toDataURL(siteVisitNumber);
    doc.addImage(qrCodeDataUrl, 'PNG', 170, 10, 30, 30);
    doc.setFontSize(8);
    doc.text(`Site Visit: ${siteVisitNumber}`, 170, 45);
  
    // Title and Header
    doc.setFontSize(24);
    doc.setTextColor(0, 51, 102);
    doc.text(project.name, 105, 30, null, null, 'center');
    
    // Project Details
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const projectDetails = [
      `Status: ${project.status}`,
      `Budget: ${project.metrics.totalBudget}`,
      `Spent: ${project.metrics.spentBudget}`,
      `Current Phase: ${project.phases[project.currentPhase].name}`,
      `Start Date: ${project.metrics.startDate}`,
      `Expected Completion: ${project.metrics.endDate}`,
      `Project Manager: ${project.assignedTeam.find(m => m.designation === 'Project Manager').name}`,
      `Site Area: ${project.metrics.siteArea}`,
      `Building Type: ${project.metrics.buildingType}`,
      `Zoning: ${project.metrics.zoning}`,
      `Permit Number: ${project.metrics.permitNumber}`
    ];
    projectDetails.forEach((detail, index) => {
      doc.text(detail, 20, 50 + (index * 6));
    });
  
    // Team Members
    doc.setFontSize(14);
    doc.setTextColor(0, 51, 102);
    doc.text('Project Team', 20, 120);
    const teamData = project.assignedTeam.map(member => [
      member.name, 
      member.designation,
      `${member.completed}/${member.tasks} Tasks`
    ]);
    doc.autoTable({
      startY: 125,
      head: [['Name', 'Role', 'Tasks Status']],
      body: teamData,
      theme: 'grid',
      headStyles: { fillColor: [0, 51, 102], textColor: 255 },
      styles: { fontSize: 9 },
    });
  
    // Construction Progress
    doc.addPage();
    doc.setFontSize(14);
    doc.setTextColor(0, 51, 102);
    doc.text('Construction Progress', 20, 20);
    const progressData = project.phases.map(phase => [
      phase.name,
      phase.status,
      `${phase.progress}%`
    ]);
    doc.autoTable({
      startY: 25,
      head: [['Phase', 'Status', 'Progress']],
      body: progressData,
      theme: 'striped',
      headStyles: { fillColor: [0, 51, 102], textColor: 255 },
      styles: { fontSize: 9 },
    });
  
    // Tasks Status
    doc.setFontSize(14);
    doc.text('Tasks Status', 20, 100);
    const tasksData = project.tasks.map(task => [
      task.title,
      task.status,
      task.assignee,
      task.deadline
    ]);
    doc.autoTable({
      startY: 105,
      head: [['Task', 'Status', 'Assignee', 'Deadline']],
      body: tasksData,
      theme: 'grid',
      headStyles: { fillColor: [0, 51, 102], textColor: 255 },
      styles: { fontSize: 9 },
    });

    // Comments and Updates
    doc.addPage();
    doc.setFontSize(14);
    doc.text('Recent Comments', 20, 20);
    const commentsData = project.comments.map(comment => [
      comment.author,
      comment.text,
      comment.date
    ]);
    doc.autoTable({
      startY: 25,
      head: [['Author', 'Comment', 'Date']],
      body: commentsData,
      theme: 'grid',
      headStyles: { fillColor: [0, 51, 102], textColor: 255 },
      styles: { fontSize: 9 },
    });
  
    // Save the PDF
    doc.save(`${project.name}_Project_Report.pdf`);
  }, [project]);

  const getMarkerIcon = useCallback((status) => {
    const color = status === 'Completed' ? '#10B981' : 
                  status === 'In Progress' ? '#6366F1' : '#F59E0B';
    return L.divIcon({
      className: 'custom-icon',
      html: `<div style="
        width: 30px; 
        height: 30px; 
        border-radius: 50%; 
        background-color: ${color}; 
        border: 3px solid white; 
        box-shadow: 0 2px 4px rgba(0,0,0,0.2); 
        display: flex; 
        align-items: center; 
        justify-center; 
        font-weight: bold; 
        color: white;
      ">${project.name[0]}</div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });
  }, [project]);

  // First, add these variants for the animations
  const stepVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: index * 0.2,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  const detailVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  // Add this new function for mobile timeline rendering
  const renderMobileTimeline = () => {
    return (
      <div className="p-4 space-y-4 pb-24"> {/* Added pb-24 for bottom nav spacing */}
        {project.phases.map((phase, index) => (
          <div 
            key={index}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm"
          >
            {/* Phase Header */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">{phase.name}</h3>
                <StatusBadge status={phase.status} isMobile={true} />
              </div>
              
              {/* Dates */}
              <div className="flex items-center text-xs text-gray-500 mb-3">
                <Calendar className="w-3.5 h-3.5 mr-1" />
                <span>{phase.startDate} - {phase.endDate}</span>
              </div>

              {/* Progress Bar */}
              <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className={`
                    absolute h-full transition-all duration-500
                    ${phase.status === 'Completed' ? 'bg-emerald-500' :
                      phase.status === 'In Progress' ? 'bg-violet-500' :
                      'bg-gray-300'}
                  `}
                  style={{ width: `${phase.progress}%` }}
                />
              </div>

              {/* Progress Text */}
              <div className="mt-2 text-right">
                <span className="text-xs font-medium text-gray-600">
                  {phase.progress}% Complete
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (!project) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-violet-50">
      <div className="flex flex-col items-center">
        <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-indigo-600 font-medium">Loading project details...</p>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
              <Link 
                to="/projects" 
                className="hover:text-indigo-600 transition-colors flex items-center font-medium"
              >
                <Home className="w-4 h-4 mr-1.5" />
                Projects
              </Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-indigo-600 font-medium">{project.name}</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {project.name}
            </h1>
            <StatusBadge status={project.status} isMobile={isMobile} />
          </div>
          <div className="hidden md:flex gap-3">
            <button
              onClick={generatePDF}
              className="inline-flex items-center px-4 py-2 
                border border-gray-200 text-gray-700
                rounded-xl hover:border-indigo-600 hover:text-indigo-600 
                transition-all duration-200 font-medium bg-white/80"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </button>
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-4 py-2 
                bg-indigo-600 text-white rounded-xl 
                hover:bg-indigo-700 transition-all duration-200
                font-medium shadow-sm hover:shadow-md"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit Project
            </button>
          </div>
        </div>

        {/* Desktop Navigation - Only show on larger screens */}
        <div className="hidden md:block border-b mb-8">
          <div className="flex justify-center gap-4 pb-4">
            {navItems.map((item) => (
              <NavButton 
                key={item.id}
                active={activeTab === item.id} 
                onClick={() => setActiveTab(item.id)}
                icon={item.icon}
                count={item.count}
              >
                {item.label}
              </NavButton>
            ))}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 backdrop-blur-sm">
          <GlassCard className="w-full max-w-2xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">Edit Project Details</h2>
              <button 
                onClick={() => setIsEditing(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={editedDetails.description}
                  onChange={(e) => setEditedDetails({...editedDetails, description: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 
                    focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                    transition-all duration-200 text-gray-800"
                  rows="4"
                  placeholder="Project description..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Budget</label>
                <input
                  type="text"
                  value={editedDetails.budget}
                  onChange={(e) => setEditedDetails({...editedDetails, budget: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 
                    focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                    transition-all duration-200 text-gray-800"
                  placeholder="Enter budget amount..."
                />
              </div>
              <div className="flex justify-end gap-3 mt-8">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-6 py-2 border border-gray-200 text-gray-600 rounded-xl 
                    hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleEditSubmit();
                    setIsEditing(false);
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-xl 
                    hover:bg-indigo-700 transition-all duration-200 font-medium
                    shadow-sm hover:shadow-md"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </GlassCard>
        </div>
      )}

      {/* Main Content Area */}
      <div className={`${isMobile ? 'pb-20' : ''}`}>
        {activeTab === 'overview' && (
          <>
            {isMobile ? (
              <div className="flex justify-center mb-6">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowWarningModal(true)}
                  className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl 
                    shadow-lg hover:shadow-xl transition-all duration-200 
                    flex items-center gap-2 text-sm font-medium"
                >
                  <Rocket className="w-4 h-4" />
                  Launch Project
                </motion.button>
              </div>
            ) : (
              <div className="flex justify-end mb-6">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowWarningModal(true)}
                  className="relative overflow-visible px-6 py-3 bg-gradient-to-r 
                    from-indigo-600 to-violet-600 text-white rounded-xl shadow-lg 
                    hover:shadow-xl transition-all duration-200 flex items-center gap-2
                    hover:-translate-y-1"
                >
                  <Rocket className="w-5 h-5" />
                  Launch New Site
                </motion.button>
              </div>
            )}

            {/* Key Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <GlassCard className="p-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-indigo-600/10 rounded-xl 
                    flex items-center justify-center">
                    <Activity className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500">Progress</p>
                    <h3 className="text-2xl font-bold text-gray-800">
                      {project.metrics.progress}%
                    </h3>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="p-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-emerald-600/10 rounded-xl 
                    flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500">Budget Spent</p>
                    <h3 className="text-2xl font-bold text-gray-800">
                      {project.metrics.budgetSpent}%
                    </h3>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="p-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-violet-600/10 rounded-xl 
                    flex items-center justify-center">
                    <Timer className="w-6 h-6 text-violet-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500">Days Left</p>
                    <h3 className="text-2xl font-bold text-gray-800">
                      {project.metrics.daysLeft}
                    </h3>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="p-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-amber-600/10 rounded-xl 
                    flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm text-gray-500">Risk Level</p>
                    <h3 className="text-2xl font-bold text-gray-800">
                      {project.metrics.riskLevel}
                    </h3>
                  </div>
                </div>
              </GlassCard>
            </div>
{/* Project Details and Map */}
<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <GlassCard className="p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-blue-700" />
                    Project Details
                  </h2>
                  <div className="space-y-5">
                    <div className="bg-gray-50/80 rounded-xl p-4">
                      <h3 className="text-sm text-gray-500 mb-1">Description</h3>
                      <p className="text-gray-800">{project.description}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50/80 rounded-xl p-4">
                        <h3 className="text-sm text-gray-500 mb-1">Budget</h3>
                        <div className="flex items-center">
                          <DollarSign className="w-4 h-4 text-indigo-600 mr-1" />
                          <p className="text-gray-800 font-semibold">{project.metrics.totalBudget}</p>
                        </div>
                      </div>
                      <div className="bg-gray-50/80 rounded-xl p-4">
                        <h3 className="text-sm text-gray-500 mb-1">Spent</h3>
                        <div className="flex items-center">
                          <DollarSign className="w-4 h-4 text-emerald-600 mr-1" />
                          <p className="text-gray-800 font-semibold">{project.metrics.spentBudget}</p>
                        </div>
                      </div>
                      <div className="bg-gray-50/80 rounded-xl p-4">
                        <h3 className="text-sm text-gray-500 mb-1">Start Date</h3>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 text-indigo-600 mr-1" />
                          <p className="text-gray-800 font-semibold">{project.metrics.startDate}</p>
                        </div>
                      </div>
                      <div className="bg-gray-50/80 rounded-xl p-4">
                        <h3 className="text-sm text-gray-500 mb-1">End Date</h3>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 text-rose-600 mr-1" />
                          <p className="text-gray-800 font-semibold">{project.metrics.endDate}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </GlassCard>

                <GlassCard className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center">
                      <Target className="w-5 h-5 mr-2 text-indigo-600" />
                      Phase Status
                    </h2>
                  </div>
                  <div className="space-y-4">
                    {project.phases.map((phase, index) => (
                      <div 
                        key={index}
                        className="flex items-center bg-gray-50/80 rounded-xl p-4"
                      >
                        <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mr-4 shadow-sm">
                          {index === 0 ? (
                            <MapPin className="w-6 h-6 text-purple-600" />
                          ) : index === 1 ? (
                            <Settings className="w-6 h-6 text-blue-600" />
                          ) : index === 2 ? (
                            <Construction className="w-6 h-6 text-orange-600" />
                          ) : (
                            <Target className="w-6 h-6 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="font-medium text-gray-800">{phase.name}</h3>
                              <p className="text-sm text-gray-500">
                                {phase.startDate} - {phase.endDate}
                              </p>
                            </div>
                            <StatusBadge status={phase.status} isMobile={isMobile} />
                          </div>
                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all duration-500
                                ${phase.status === 'Completed' ? 'bg-emerald-500' :
                                  phase.status === 'In Progress' ? 'bg-violet-500' :
                                  'bg-gray-300'}
                              `}
                              style={{ width: `${phase.progress}%` }}
                            />
                          </div>
                          <div className="flex justify-end mt-1">
                            <span className="text-sm text-gray-500">{phase.progress}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              </div>

              <GlassCard className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center">
                    <MapPin className="w-5 h-5 mr-2 text-indigo-600" />
                    Project Location
                  </h2>
                </div>
                <div className="h-[600px] rounded-xl overflow-hidden shadow-lg">
                  <MapContainer 
                    center={[project.location.latitude, project.location.longitude]} 
                    zoom={13} 
                    className="h-full w-full"
                    zoomControl={false}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <Marker 
                      position={[project.location.latitude, project.location.longitude]}
                      icon={getMarkerIcon(project.status)}
                    >
                      <Popup>
                        <div className="p-2">
                          <h3 className="font-bold mb-2">{project.name}</h3>
                          <div className="flex flex-col gap-2">
                            <StatusBadge status={project.status} isMobile={isMobile} />
                            <div className="text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                Location Details
                              </div>
                            </div>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>

                <div className="mt-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50/80 rounded-xl p-4">
                      <h3 className="text-sm text-gray-500 mb-1">Site Area</h3>
                      <p className="text-gray-800 font-semibold">{project.metrics.siteArea}</p>
                    </div>
                    <div className="bg-gray-50/80 rounded-xl p-4">
                      <h3 className="text-sm text-gray-500 mb-1">Building Type</h3>
                      <p className="text-gray-800 font-semibold">{project.metrics.buildingType}</p>
                    </div>
                    <div className="bg-gray-50/80 rounded-xl p-4">
                      <h3 className="text-sm text-gray-500 mb-1">Zoning</h3>
                      <p className="text-gray-800 font-semibold">{project.metrics.zoning}</p>
                    </div>
                    <div className="bg-gray-50/80 rounded-xl p-4">
                      <h3 className="text-sm text-gray-500 mb-1">Permit Number</h3>
                      <p className="text-gray-800 font-semibold">{project.metrics.permitNumber}</p>
                    </div>
                  </div>
                </div>
              </GlassCard>
            </div>
          </>
        )}
{activeTab === 'timeline' && (
  <div className="flex-1 overflow-auto">
    {isMobile ? (
      renderMobileTimeline()
    ) : (
      <GlassCard className="p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-indigo-600" />
          Project Timeline
        </h2>
        <div className="space-y-6">
          {project.phases.map((phase, index) => (
            <div key={index} className="relative">
              <div className="flex items-center gap-6">
                <div className="w-48">
                  <h3 className="font-semibold text-gray-800">{phase.name}</h3>
                  <p className="text-sm text-gray-500 flex items-center mt-1">
                    <Calendar className="w-4 h-4 mr-1.5" />
                    {phase.startDate} - {phase.endDate}
                  </p>
                </div>
                <div className="flex-1">
                  <div className="relative h-4 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className={`
                        absolute h-full transition-all duration-500
                        ${phase.status === 'Completed' ? 'bg-emerald-500' :
                          phase.status === 'In Progress' ? 'bg-violet-500' :
                          'bg-gray-300'}
                      `}
                      style={{ width: `${phase.progress}%` }}
                    />
                  </div>
                  <div className="mt-2 flex justify-between items-center">
                    <StatusBadge status={phase.status} isMobile={false} />
                    <span className="text-sm font-medium text-gray-600">{phase.progress}% Complete</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </GlassCard>
    )}
  </div>
)}


{activeTab === 'phases' && (
    <PhasesTab 
      project={project} 
      activePhase={activePhase} 
      setActivePhase={setActivePhase}
      handleFileUpload={handleFileUpload}
    />
)}


        {activeTab === 'files' && (
          <GlassCard className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-indigo-600" />
              Project Files
            </h2>
            
            <div className="space-y-6">
              {project.phases.map((phase, index) => (
                <div key={index} className="space-y-4">
                  <div className="flex items-center">
                    <FolderOpen className="w-5 h-5 text-indigo-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-800">{phase.name}</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-7">
                    {phase.files.map((file, fileIndex) => (
                      <div 
                        key={fileIndex}
                        className="flex items-center justify-between p-4 bg-gray-50/80 
                          rounded-xl hover:shadow-md transition-all"
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div className="ml-4">
                            <p className="font-medium text-gray-800">{file.name}</p>
                            <p className="text-sm text-gray-500">
                              {file.size} • {file.uploadDate}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button className="p-2 text-gray-400 hover:text-indigo-600 
                            hover:bg-indigo-50 rounded-lg transition-colors">
                            <Eye className="w-5 h-5" />
                          </button>
                          <button className="p-2 text-gray-400 hover:text-rose-600 
                            hover:bg-rose-50 rounded-lg transition-colors">
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        )}

        {activeTab === 'team' && (
          <GlassCard className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              <Users className="w-5 h-5 mr-2 text-indigo-600" />
              Project Team
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {project.assignedTeam.map((member, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-sm 
                    hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <div className="w-16 h-16 bg-indigo-50 rounded-xl flex items-center 
                        justify-center shadow-sm">
                        <span className="text-xl font-bold text-indigo-600">
                          {member.name[0]}
                        </span>
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-5 h-5 
                        rounded-full border-2 border-white
                        ${index === 0 ? 'bg-emerald-400' : 
                          index === 1 ? 'bg-violet-400' : 'bg-amber-400'}`} 
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{member.name}</h3>
                      <p className="text-sm text-gray-500">{member.designation}</p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Tasks</span>
                      <span className="font-medium">{member.tasks}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-gray-500">Completed</span>
                      <span className="font-medium">{member.completed}</span>
                    </div>
                    <div className="mt-2 w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-indigo-600 rounded-full"
                        style={{ width: `${(member.completed / member.tasks) * 100}%` }}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4 flex gap-2">
                    <button className="flex-1 py-2 text-sm text-indigo-600 bg-indigo-50 
                      rounded-lg hover:bg-indigo-100 transition-colors">
                      Message
                    </button>
                    <button className="flex-1 py-2 text-sm text-gray-600 bg-gray-50 
                      rounded-lg hover:bg-gray-100 transition-colors">
                      View Profile
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        )}

        {activeTab === 'tasks' && (
          <GlassCard className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center">
                <CheckCircle className="w-5 h-5 mr-2 text-indigo-600" />
                Project Tasks
              </h2>
              <button className="px-4 py-2 bg-indigo-600 text-white rounded-xl 
                hover:bg-indigo-700 transition-all duration-200 flex items-center font-medium">
                <Plus className="w-4 h-4 mr-1.5" />
                Add Task
              </button>
            </div>
            <div className="space-y-4">
              {project.tasks.map((task) => (
                <div 
                  key={task.id} 
                  className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md 
                    transition-all duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center
                        ${task.status === 'Completed' ? 'bg-emerald-50' :
                          task.status === 'In Progress' ? 'bg-violet-50' :
                          task.status === 'Not Started' ? 'bg-gray-50' :
                          'bg-amber-50'}`
                      }>
                        {task.status === 'Completed' ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        ) : task.status === 'In Progress' ? (
                          <Clock3 className="w-5 h-5 text-violet-600" />
                        ) : task.status === 'Not Started' ? (
                          <Ban className="w-5 h-5 text-gray-600" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-800">{task.title}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <p className="text-sm text-gray-500">Assigned to {task.assignee}</p>
                          <span className="text-sm text-gray-400">•</span>
                          <p className="text-sm text-gray-500">Due {task.deadline}</p>
                        </div>
                      </div>
                    </div>
                    <select
                      value={task.status}
                      onChange={(e) => handleTaskStatusChange(task.id, e.target.value)}
                      className="px-4 py-2 rounded-lg text-sm font-medium 
                        border border-gray-200 focus:outline-none focus:ring-2 
                        focus:ring-indigo-500 bg-white text-gray-700
                        hover:border-indigo-500 transition-colors"
                    >
                      <option value="Not Started">Not Started</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Pending">Pending</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        )}

        {activeTab === 'comments' && (
          <GlassCard className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2 text-indigo-600" />
              Project Comments
            </h2>
            <div className="space-y-6">
              <div className="space-y-4">
                {project.comments.map((comment) => (
                  <div 
                    key={comment.id} 
                    className="bg-white rounded-xl p-4 shadow-sm"
                  >
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center 
                          justify-center">
                          <span className="text-sm font-bold text-indigo-600">
                            {comment.author[0]}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-800">{comment.author}</h3>
                          <p className="text-xs text-gray-500">{comment.date}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="p-2 text-gray-400 hover:text-indigo-600 
                          hover:bg-indigo-50 rounded-lg transition-colors">
                          <MessageSquare className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-rose-600 
                          hover:bg-rose-50 rounded-lg transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-gray-700 pl-13">{comment.text}</p>
                  </div>
                ))}
              </div>

              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCommentSubmit(e.target.comment.value);
                  e.target.comment.value = '';
                }}
                className="mt-6"
              >
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <textarea
                    name="comment"
                    placeholder="Add a comment..."
                    className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 
                      focus:ring-indigo-500 focus:border-transparent resize-none
                      text-gray-700 placeholder-gray-400"
                    rows="3"
                  />
                  <div className="mt-3 flex justify-end">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg 
                        hover:bg-indigo-700 transition-all duration-200 font-medium
                        flex items-center"
                    >
                      <MessageSquare className="w-4 h-4 mr-1.5" />
                      Post Comment
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </GlassCard>
        )}
      </div>

      {/* Launch Modal */}
      <AnimatePresence>
        {showWarningModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 
              flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-8 max-w-md w-full mx-4"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Launch New Site</h3>
                  <p className="text-gray-500">Are you sure you want to proceed?</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-6">
                {project.status !== 'Completed' 
                  ? "This project is not marked as completed. Launching now might result in incomplete features or missing requirements."
                  : "This will initiate the site launch process. Please ensure all requirements are met."}
              </p>

              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowWarningModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowWarningModal(false);
                    setShowLaunchModal(true);
                    const timer = setInterval(() => {
                      setLaunchStep(prev => {
                        if (prev === launchSteps.length - 1) {
                          clearInterval(timer);
                          return prev;
                        }
                        return prev + 1;
                      });
                    }, 1500);
                  }}
                  className="px-6 py-2 bg-amber-600 text-white rounded-lg 
                    hover:bg-amber-700 transition-colors"
                >
                  Proceed Anyway
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        {showLaunchModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 
              flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-8 max-w-md w-full mx-4"
            >
              {!isLaunching ? (
                <>
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8 text-center">
                    <Cover>Launch New Site</Cover>
                  </h3>
                  <div className="mb-8">
                    <p className="text-gray-600 mb-4">
                      Current number of sites: {currentSiteNumber}
                    </p>
                    <div className="space-y-4">
                      {launchSteps.map((step, index) => (
                        <motion.div 
                          key={index}
                          initial="hidden"
                          animate={index <= launchStep ? "visible" : "hidden"}
                          variants={stepVariants}
                          custom={index}
                          className={`flex items-center gap-3 ${
                            index <= launchStep ? 'text-indigo-600' : 'text-gray-400'
                          }`}
                        >
                          <motion.div 
                            className={`w-6 h-6 rounded-full flex items-center justify-center
                              ${index <= launchStep ? 'bg-indigo-100' : 'bg-gray-100'}`}
                            animate={{
                              scale: index === launchStep ? [1, 1.2, 1] : 1,
                              transition: { duration: 0.3 }
                            }}
                          >
                            {index < launchStep ? (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.3 }}
                              >
                                <Check className="w-4 h-4" />
                              </motion.div>
                            ) : (
                              <span className="text-sm">{index + 1}</span>
                            )}
                          </motion.div>
                          <div>
                            <span className="text-sm font-medium">{step.text}</span>
                            <AnimatePresence>
                              {index <= launchStep && (
                                <motion.div
                                  initial="hidden"
                                  animate="visible"
                                  exit="hidden"
                                  variants={detailVariants}
                                  className="text-xs text-gray-500 mt-0.5 overflow-hidden"
                                >
                                  {index === 0 && "Location and permits verified"}
                                  {index === 1 && "All design documents approved"}
                                  {index === 2 && "Site build-out complete"}
                                  {index === 3 && "Equipment and systems tested"}
                                  {index === 4 && "Final checks and documentation complete"}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                  {launchStep === launchSteps.length - 1 && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setIsLaunching(true);
                        confetti({
                          particleCount: 100,
                          spread: 70,
                          origin: { y: 0.6 }
                        });
                        
                        // Animate site number increase
                        const startNumber = currentSiteNumber;
                        const endNumber = currentSiteNumber + 1;
                        let current = startNumber;
                        
                        const animateNumber = () => {
                          if (current < endNumber) {
                            current += 0.1;
                            setCurrentSiteNumber(Math.floor(current));
                            requestAnimationFrame(animateNumber);
                          } else {
                            setCurrentSiteNumber(endNumber);
                            setTimeout(() => {
                              setShowLaunchModal(false);
                              setIsLaunching(false);
                              setLaunchStep(0);
                            }, 2000);
                          }
                        };
                        
                        requestAnimationFrame(animateNumber);
                      }}
                      className="w-full px-6 py-3 bg-indigo-600 text-white rounded-xl
                        hover:bg-indigo-700 transition-colors relative overflow-visible
                        after:content-[''] after:absolute after:rounded-xl after:inset-0
                        after:bg-indigo-400/40 after:z-[-1] after:transition
                        after:!duration-500 hover:after:scale-150 hover:after:opacity-0"
                    >
                      Confirm Launch
                    </motion.button>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <motion.h3
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-4xl font-bold bg-clip-text text-transparent
                      bg-gradient-to-r from-indigo-600 to-violet-600"
                  >
                    Launching Site {currentSiteNumber + 1}
                  </motion.h3>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Bottom Navigation - Only show on mobile */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 pb-safe z-40 md:hidden">
          <nav className="max-w-lg mx-auto flex items-center justify-between">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.id === 'more') {
                    setShowMoreMenu(!showMoreMenu);
                  } else {
                    setActiveTab(item.id);
                  }
                }}
                className={`
                  relative flex flex-col items-center py-3 px-3
                  transition-colors duration-200 group
                  ${activeTab === item.id 
                    ? 'text-indigo-600' 
                    : 'text-gray-500 hover:text-gray-900'
                  }
                `}
              >
                {item.id === 'more' && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 
                    text-white text-xs font-bold rounded-full flex items-center 
                    justify-center shadow-sm"
                  >
                    {5 + 3}
                  </span>
                )}
                <item.icon className="w-6 h-6" />
                <span className="text-xs mt-1">{item.label}</span>
                <div className={`
                  absolute -top-[1px] left-1/2 -translate-x-1/2
                  w-8 h-1 rounded-full transition-all duration-200
                  ${activeTab === item.id ? 'bg-indigo-600 scale-100' : 'scale-0'}
                `} />
              </button>
            ))}
          </nav>

          {/* More Menu Popup */}
          <AnimatePresence>
            {showMoreMenu && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="absolute bottom-full right-4 mb-2 w-56 bg-white rounded-xl shadow-lg 
                  border border-gray-100 overflow-hidden"
              >
                <div className="p-2 space-y-1">
                  <button
                    onClick={() => {
                      setActiveTab('tasks');
                      setShowMoreMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg
                      text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <CheckCircle className="w-5 h-5 text-gray-400" />
                    <span className="text-sm font-medium">Tasks</span>
                    <span className="ml-auto bg-red-100 text-red-600 text-xs font-bold
                      px-2 py-1 rounded-full">5</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setActiveTab('comments');
                      setShowMoreMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg
                      text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <MessageSquare className="w-5 h-5 text-gray-400" />
                    <span className="text-sm font-medium">Comments</span>
                    <span className="ml-auto bg-red-100 text-red-600 text-xs font-bold
                      px-2 py-1 rounded-full">3</span>
                  </button>

                  <div className="h-px bg-gray-100 my-1" />

                  <button
                    onClick={() => {
                      generatePDF();
                      setShowMoreMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg
                      text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Download className="w-5 h-5 text-gray-400" />
                    <span className="text-sm font-medium">Export Report</span>
                  </button>

                  <button
                    onClick={() => {
                      setIsEditing(true);
                      setShowMoreMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg
                      text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Edit2 className="w-5 h-5 text-gray-400" />
                    <span className="text-sm font-medium">Edit Project</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;